import { Button, Grid, Paper } from "@material-ui/core";
import { ConditionType, formTypeLabels, questionTypes } from "features/Forms/formBuilder/formBuilderConsts";
import { createRef, useMemo, useRef, useState } from "react";
import FormFailure from "../FormFailure";
import { Question } from "./components/Question";
import { EditFormOutputStyle } from "./style";

// Distance (px) below the current scroll position a question must be to count
// as "ahead" of the user. Keeps a question right at the top of the viewport
// from being picked again on repeated clicks.
const SCROLL_AHEAD_BUFFER_PX = 16;

export const EditFormOutputFields = ({
    customFormResponse,
    allQuestions,
    formAnswers,
    setFormAnswers,
    formType,
    showFailuresOnly,
    setShowFailuresOnly,
    getIsFailure,
    handleSave,
    isSaving,
    resetForm
}) => {
    const classes = EditFormOutputStyle();

    // The selected option object(s) for a choice-type question. Both DropdownComponent
    // and MultipleChoiceComponent pass through the full option definition on selection
    // (see onChange in each), so `requirePhoto`/`requireNote` set on that option in the
    // form builder ride along on the answer itself — no separate lookup needed.
    const getSelectedOptionValues = (q, answer) => {
        if (q?.questionType === questionTypes.MultipleChoice) {
            return Array.isArray(answer?.multipleChoiceValue) ? answer.multipleChoiceValue : [];
        }
        if (q?.questionType === questionTypes.Dropdown) {
            return answer?.dropdownValue ? [answer.dropdownValue] : [];
        }
        return [];
    }

    const answerRequiresPhoto = (q, answer) => !!q?.includePhoto && getSelectedOptionValues(q, answer).some(v => !!v?.requirePhoto);
    const answerRequiresNote = (q, answer) => !!q?.includeNotes && getSelectedOptionValues(q, answer).some(v => !!v?.requireNote);

    const isQuestionValid = (q) => {
        const answer = formAnswers?.[q.id];
        let hasAnswer;
        switch (q?.questionType) {
            case questionTypes.Image:
                return (answer?.pictureObj || answer?.pictureUrl) ? true : false;
            case questionTypes.MultipleChoice:
                hasAnswer = Array.isArray(answer?.multipleChoiceValue) && answer.multipleChoiceValue.length > 0;
                break;
            case questionTypes.Freetext:
                hasAnswer = (answer?.freetextValue && answer.freetextValue != `""`) ? true : false;
                break;
            case questionTypes.Date:
                hasAnswer = (answer?.dateValue && answer.dateValue != `""`) ? true : false;
                break;
            case questionTypes.Dropdown:
                hasAnswer = (answer?.dropdownValue && answer.dropdownValue != `""`) ? true : false;
                break;
            default:
                hasAnswer = (answer?.values && answer.values != `""`) ? true : false;
        }
        if (!hasAnswer) return false;
        // The primary value is there, but the selected option itself may still demand
        // a photo/note (e.g. "Damaged" requires a photo, "Clean" doesn't).
        if (answerRequiresPhoto(q, answer) && !(answer?.pictureObj || answer?.pictureUrl)) return false;
        if (answerRequiresNote(q, answer) && !answer?.note) return false;
        return true;
    }

    // "Only show question when: <question> is <values>" — questionCondition is the raw
    // JSON the form builder writes (see FormConditions.js / FormQuestionAggregate.ParseConditions
    // on the API side); nothing evaluates it at fill-time today, so a question whose trigger
    // condition isn't met would otherwise be flagged as "required but missing" forever.
    const parseJson = (raw, fallback) => {
        try {
            return raw ? JSON.parse(raw) ?? fallback : fallback;
        } catch {
            return fallback;
        }
    }

    const conditionValueMatches = (answerOption, expectedOption) => {
        const answerKeys = [answerOption?.id, answerOption?.value, answerOption?.name]
            .filter(v => v !== undefined && v !== null).map(String);
        const expectedKeys = [expectedOption?.id, expectedOption?.name]
            .filter(v => v !== undefined && v !== null).map(String);
        return answerKeys.some(k => expectedKeys.includes(k));
    }

    const isConditionMet = (q) => {
        const conditions = parseJson(q?.questionCondition, []);
        if (!conditions?.length) return true; // no condition configured -> always applicable

        return conditions.every(condition => {
            // Only "based on another question's answer" is wired up in the builder today;
            // don't block on a condition kind we can't evaluate.
            if (condition?.conditionType && condition.conditionType !== ConditionType.QUESTION) return true;

            const expectedValues = parseJson(condition?.values, []);
            if (!expectedValues.length) return true;

            const referencedQuestion = allQuestions?.find(other => other.id === condition.questionId);
            const referencedAnswer = formAnswers?.[condition.questionId];
            const selectedValues = getSelectedOptionValues(referencedQuestion, referencedAnswer);
            return selectedValues.some(sel => expectedValues.some(exp => conditionValueMatches(sel, exp)));
        });
    }

    // Single source of truth: both the Save button and the "Show pending" button
    // below must agree on exactly which questions are blocking completion.
    const isQuestionPending = (q) => !!q?.required && isConditionMet(q) && !isQuestionValid(q);

    const isValid = () => {
        return allQuestions?.every(q => !isQuestionPending(q))
    }

    const questionRefs = useRef({});
    const getQuestionRef = (questionId) => {
        if (!questionRefs.current[questionId]) {
            questionRefs.current[questionId] = createRef();
        }
        return questionRefs.current[questionId];
    };

    const [highlightPending, setHighlightPending] = useState(false);

    const pendingQuestions = useMemo(
        () => (allQuestions ?? []).filter(isQuestionPending),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [allQuestions, formAnswers]
    );
    const pendingQuestionIds = useMemo(() => new Set(pendingQuestions.map(q => q.id)), [pendingQuestions]);

    const goToNextPendingQuestion = () => {
        setHighlightPending(true);

        const positions = pendingQuestions
            .map(q => questionRefs.current[q.id]?.current)
            .filter(Boolean)
            .map(node => ({ node, top: node.getBoundingClientRect().top + window.scrollY }))
            .sort((a, b) => a.top - b.top);

        if (!positions.length) return;

        const currentScrollTop = window.scrollY + SCROLL_AHEAD_BUFFER_PX;
        // Nearest pending question below where the user currently is; if every
        // pending question is above (e.g. user scrolled past all of them), wrap
        // around to the first one instead of doing nothing.
        const target = positions.find(p => p.top > currentScrollTop) ?? positions[0];

        target.node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    return (
        <>
            <Paper variant='outlined' className={classes.reportContainer}>
                <Grid container alignItems='center' justifyContent="space-between" style={{ padding: '1em', background: '#efefef' }}>
                    <Grid item>
                        <div>{`Edit ${formTypeLabels[formType]}`}</div>
                    </Grid>
                    <Grid item>
                        <FormFailure customFormResponse={customFormResponse} showFailuresOnly={showFailuresOnly} setShowFailuresOnly={setShowFailuresOnly} />
                    </Grid>
                </Grid>
                <form id="editFormOutput" className={`${classes.form}`}>
                    {allQuestions?.map((q, index) => <>
                        <Question
                            key={q.id}
                            question={q}
                            formAnswers={formAnswers}
                            setFormAnswers={setFormAnswers}
                            showFailuresOnly={showFailuresOnly}
                            getIsFailure={getIsFailure}
                            containerRef={getQuestionRef(q.id)}
                            isPending={highlightPending && pendingQuestionIds.has(q.id)}
                        />
                    </>)}
                </form>
                <Paper className={classes.reportActions} >
                    <Grid container justifyContent="flex-end" alignItems="center" spacing={2}>
                        {pendingQuestions.length > 0 &&
                            <Grid item>
                                <Button onClick={goToNextPendingQuestion} variant="outlined" color="secondary">
                                    {`Show pending (${pendingQuestions.length})`}
                                </Button>
                            </Grid>
                        }
                        <Grid item>
                            <Button onClick={() => resetForm()} variant="outlined" color="primary" disabled={isSaving}>
                                Cancel
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button
                                onClick={handleSave}
                                color="primary"
                                variant="contained"
                                disabled={isSaving || !isValid()}
                            >
                                {isSaving ? 'Saving...' : 'Save'}
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>
            </Paper>
        </>
    )
}